import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil } from 'rxjs';
import { MaterialModule } from 'src/app/material.module';
import { IndianCurrencyPipe } from 'src/app/pipe/indian-currency.pipe';
import { TranslationUtils } from 'src/app/common/utils/translation.utils';
import { DrugBatchItem } from 'src/app/models/pharmacy.model';
import { PharmacyService } from '../pharmacy.service';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { MY_FORMATS } from 'src/app/common/dateFormat';
import { AddBatchDialogData } from '../pharmacy.type';

@Component({
  selector: 'app-add-batch-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    TablerIconsModule,
    TranslateModule,
    ReactiveFormsModule,
    IndianCurrencyPipe,
  ],
  providers: [
    IndianCurrencyPipe,
    DatePipe,
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
  templateUrl: './add-batch-dialog.component.html',
})
export class AddBatchDialogComponent implements OnInit, OnDestroy {

  batchForm: FormGroup;
  drugName: string;
  viewOnly: boolean;
  batches: DrugBatchItem[] = [];
  batchDisplayedColumns: string[] = ['batchNo', 'expiryDate', 'quantity', 'purchasePrice'];
  private destroy$ = new Subject<void>();
  minDate: Date = new Date();

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<AddBatchDialogComponent>,
    private pharmacyService: PharmacyService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private translationUtils: TranslationUtils,
    @Inject(MAT_DIALOG_DATA) private data: AddBatchDialogData,
  ) {
    this.drugName = data.drugName;
    this.viewOnly = data.viewOnly ?? false;
    this.batches = data.batches ?? [];
  }

  ngOnInit(): void {
    this.initForm();
  }

  private initForm(): void {
    this.batchForm = this.fb.group({
      drugInventoryId: [this.data.drugInventoryId, Validators.required],
      batchNo: ['', Validators.required],
      expiryDate: [null, Validators.required],
      quantity: [null, [Validators.required, Validators.min(1)]],
      purchasePrice: [null],
      sellingPrice: [null]
    });
  }

  onSave(): void {
    if (this.batchForm.invalid) return;

    const payload = this.batchForm.getRawValue();

    this.pharmacyService.addBatch(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        if (response.isSuccess) {
          this.dialogRef.close(response);
          this.toastr.success(
            this.translationUtils.getTranslatedMessage('COMMON.CREATE', this.translate.instant('PHARMACY.BATCH'))
          );
        } else {
          const allErrors = Object.values(response.errors).flat();
          this.toastr.error(allErrors.join('\n'));
        }
      });
  }

  onClose(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
